import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Box, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { Button } from "components/Button/Button";
import { Heading } from "components/Heading/Heading";
import { Input } from "components/Input/Input";
import { StyledContainer, StyledTableControls } from "components/ItemsListElements/ItemsListElements";
import { PageHeader } from "components/PageHeader/PageHeader";
import { Select } from "components/Select/Select";
import { Table } from "components/Table/Table";
import { getTimestamp } from "helpers/date";
import { MailLog, useMails } from "hooks/useMails";
import { filterMails, MAIL_TYPES, mailTypeLabel } from "./filterMails";
import { MailPreview } from "./MailPreview";

const row = (arg: any) => arg.row.original as MailLog;

export function Mails() {
  const { t } = useTranslation();
  const { mails, loading, error, truncated, fetch } = useMails();
  const [from, setFrom] = useState(() => dayjs().subtract(6, "day").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<MailLog>();

  useEffect(() => {
    if (from && to) fetch(from, to);
  }, [fetch, from, to]);

  const columns = useMemo(
    () => [
      {
        Header: t("common:Date"),
        id: "date",
        Cell: (arg: any) => dayjs(getTimestamp(row(arg).createdAt)).format("DD/MM/YYYY HH:mm"),
      },
      {
        Header: t("common:Status"),
        id: "status",
        Cell: (arg: any) => {
          const { status, error } = row(arg);
          return (
            <Box maxW="240px">
              <Badge colorScheme={status === "sent" ? "green" : "red"}>{status === "sent" ? "Wysłano" : "Błąd"}</Badge>
              {error && (
                <Text color="red.600" fontSize="xs" mt="4px" noOfLines={2} title={error} wordBreak="break-word">
                  {error}
                </Text>
              )}
            </Box>
          );
        },
      },
      { Header: t("common:Type"), id: "type", Cell: (arg: any) => mailTypeLabel(row(arg).type) },
      { Header: t("common:Subject"), id: "subject", Cell: (arg: any) => row(arg).subject },
      {
        Header: t("common:Recipients"),
        id: "recipients",
        Cell: (arg: any) => {
          const { to, cc } = row(arg);
          return (
            <>
              {to.map((email) => (
                <Text key={email} wordBreak="break-word">
                  {email}
                </Text>
              ))}
              {cc.map((email) => (
                <Text color="gray.500" fontSize="xs" key={email} wordBreak="break-word">
                  {t("common:Cc")}: {email}
                </Text>
              ))}
            </>
          );
        },
      },
      {
        Header: t("common:Company"),
        id: "company",
        Cell: (arg: any) => {
          const { company } = row(arg);
          return company ? (
            <Box as="span" color="sk-purple" wordBreak="break-word">
              <Link to={`/companies/${company.id}`}>{company.name ?? company.id}</Link>
            </Box>
          ) : (
            "-"
          );
        },
      },
      {
        Header: t("common:Transaction"),
        id: "transaction",
        Cell: (arg: any) => {
          const { transactionId } = row(arg);
          return transactionId ? (
            <Box as="span" color="sk-purple">
              <Link to={`/transactions/${transactionId}`}>{t("common:Open")}</Link>
            </Box>
          ) : (
            "-"
          );
        },
      },
      {
        Header: t("common:Sent by"),
        id: "user",
        Cell: (arg: any) => {
          const { user } = row(arg);
          return [user.name, user.surname].filter(Boolean).join(" ");
        },
      },
      {
        Header: "",
        id: "preview",
        Cell: (arg: any) => (
          <Box as="button" color="sk-purple" onClick={() => setPreview(row(arg))} whiteSpace="nowrap">
            {t("common:Preview")}
          </Box>
        ),
      },
    ],
    [t]
  );

  const visible = useMemo(() => filterMails(mails, { status, type, query }), [mails, status, type, query]);

  return (
    <>
      <PageHeader>
        <Heading type="h1">{t("sidebar:E-mails")}</Heading>
      </PageHeader>
      <StyledContainer>
        <StyledTableControls>
          <Box alignItems="flex-end" display="flex" flexWrap="wrap" gap="10px" mt="20px" width="100%">
            <Input
              id="from"
              label={t("transaction:From")}
              name="from"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
              topLabel
              type="date"
              value={from}
              w={170}
            />
            <Input
              id="to"
              label={t("transaction:To")}
              name="to"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
              topLabel
              type="date"
              value={to}
              w={170}
            />
            <Box width="170px">
              <Select
                label={t("common:Status")}
                name="status"
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value)}
                options={[
                  { value: "sent", label: "Wysłano" },
                  { value: "failed", label: "Błąd" },
                ]}
                placeholder={t("common:All")}
                value={status}
              />
            </Box>
            <Box width="240px">
              <Select
                label={t("common:Type")}
                name="type"
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setType(e.target.value)}
                options={Object.entries(MAIL_TYPES).map(([value, label]) => ({ value, label }))}
                placeholder={t("common:All")}
                value={type}
              />
            </Box>
            <Input
              id="query"
              label={t("common:Search")}
              name="query"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder={t("common:Subject, recipient, company")}
              topLabel
              value={query}
              w={260}
            />
            <Button design="secondary" ml="auto" onClick={() => fetch(from, to)}>
              {t("common:Refresh")}
            </Button>
          </Box>
        </StyledTableControls>
        {error && (
          <Text color="red.600" px="30px" py="10px">
            {t("common:E-mails could not be loaded")}
          </Text>
        )}
        {truncated && (
          <Text color="orange.600" px="30px" py="10px">
            {t("common:Showing the 500 newest e-mails, narrow the dates")}
          </Text>
        )}
        {!error && (
          <Table
            columns={columns}
            data={visible}
            emptyDataText={t("common:No e-mails")}
            loading={loading}
            paginated={false}
          />
        )}
      </StyledContainer>
      <MailPreview mail={preview} onClose={() => setPreview(undefined)} />
    </>
  );
}
